import React from 'react'
// libs
import styled from 'styled-components'
// components
import CheckCircle from '@mui/icons-material/CheckCircle'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
// views
import { Button, ButtonLabel, Wrapper, DueDateWrapper } from '../../../../../components/CompleteReview/views'
// themes
import colors from 'themes/colors'
// context
import { KPIContext } from 'context'
// constants
import { Modals, KPI_RESULT_TYPE_VALUES } from 'constants/index'
// hooks
import { useShowModal } from 'hooks'
// utils
import moment from 'moment'
import get from 'lodash/get'
import { filter, find, first, isEmpty, isUndefined, last, orderBy, pipe } from 'lodash/fp'
import { generateCapturedResults, generateRanges, generateResultTarget, getFieldValues, safeGetOr } from 'utils'
import { getScaleOptionValue } from 'utils/kpi'

// styled
const InfoWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 5px;
`

const SubTitle = styled.span`
  color: ${colors.darkBlue500};
  font-size: 12px;
`

const QuickResultEnter = ({ isFullModal }) => {
  const { entity, update } = React.useContext(KPIContext)

  const { kpiAddKPIResult } = entity.currentPermissions

  const { showModal, hideModal } = useShowModal({
    modalType: Modals.KPI_SET_RESULT_ROW_MODAL
  })

  if (isEmpty(entity.recurrence)) {
    return null
  }

  const { ranges } = generateRanges({ recurrence: entity.recurrence })

  const capturedResults = safeGetOr([], 'resultTarget.capturedResults')(entity)

  const dueDateOrToday =
    !!ranges &&
    pipe(
      filter(({ lastDayOfThePeriod }) => lastDayOfThePeriod.isSameOrBefore(moment(), 'day')),
      orderBy('id', 'asc'),
      filter(({ id, lastDayOfThePeriod }) => {
        const capturedResult = find(
          (result) => result.id === id && moment(result.lastDayOfThePeriod).isSame(moment(lastDayOfThePeriod), 'day')
        )(capturedResults)

        return !capturedResult
      }),
      first
    )(ranges)

  let title, subtitle

  const isDue = !!dueDateOrToday

  const lastResult = pipe(
    orderBy(({ lastDayOfThePeriod }) => moment(lastDayOfThePeriod).unix(), 'asc'),
    last,
    (result = {}) => ({
      ...result,
      value: getScaleOptionValue(result.value, entity.resultType, entity.resultTypeScale)
    })
  )(capturedResults)

  if (isDue) {
    title = `Enter result for ${dueDateOrToday.startPoint.format(
      'D MMM YY'
    )} to ${dueDateOrToday.lastDayOfThePeriod.format('D MMM YY')}`

    if (!isUndefined(get(lastResult, 'value.value'))) {
      subtitle = `Our last result was ${lastResult.value.label}`
    }
  } else {
    if (!isUndefined(get(lastResult, 'value.value'))) {
      title = `Current result is ${lastResult.value.label} for ${moment(lastResult.startPoint).format(
        'D MMM YY'
      )} to ${moment(lastResult.lastDayOfThePeriod).format('D MMM YY')}`
    }

    const nextRange = pipe(
      filter(({ lastDayOfThePeriod }) => lastDayOfThePeriod.isAfter(moment())),
      orderBy('id', 'asc'),
      first
    )(ranges)

    if (nextRange) {
      subtitle = `next result due ${nextRange.dueDate.format('D MMM YY')}`
    }
  }

  const handleQuickResultEnterClick = async () => {
    if (!isDue) {
      return
    }

    showModal({
      onSubmit: async (data) => {
        const isValueExpected = entity.resultType === KPI_RESULT_TYPE_VALUES.VALUE
        const captures = generateCapturedResults(capturedResults, dueDateOrToday, isValueExpected, data.resultValue)
        const resultTarget = generateResultTarget(entity, captures, ranges)

        try {
          await update({
            variables: {
              id: entity.id,
              input: {
                fieldValues: getFieldValues(entity.fields, { resultTarget }),
                resultTarget
              }
            }
          })

          hideModal()
        } catch (error) {
          console.log('[QuickResultEnter.handleQuickResultEnterClick]: ', error)
        }
      },
      entity
    })
  }

  return (
    <Wrapper $isFullModal={isFullModal}>
      {isDue ? (
        <CheckCircleOutline fontSize={'large'} htmlColor={colors.gray200} />
      ) : (
        <CheckCircle fontSize={'large'} htmlColor={colors.blue500} />
      )}
      <InfoWrapper>
        <DueDateWrapper>{title}</DueDateWrapper>
        <SubTitle>{subtitle}</SubTitle>
      </InfoWrapper>

      {isDue && kpiAddKPIResult && (
        <Button $isFullModal={isFullModal} onClick={handleQuickResultEnterClick}>
          <ButtonLabel>ENTER RESULT</ButtonLabel>
        </Button>
      )}
    </Wrapper>
  )
}

export default QuickResultEnter
